/****************************************************************
 * CODE Product Configurator
 ****************************************************************/

import { formatMoney } from '@shopify/theme-currency';
import './autocomplete-input.js';

const MOBILE_BREAKPOINT_WIDTH = 768;

class ProductConfigurator extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      printColor: 'black',
      numberImageSources: [],
      blacklistNumbers: [],
      blacklistNames: [],
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Create events
    this.changeEvent = new Event('change');

    // Scope global variables
    this.currentSku = 'notdefined';

    // Scope custom elements
    this.productMediaElement = document.querySelector('product-media');
    this.variantRadiosElement = document.querySelector('variant-radios');
    this.productFormElement = document.querySelector('product-form');
    this.cartNotification = document.querySelector('cart-notification');
    this.cartDrawer = document.querySelector('cart-drawer');

    // Scope elements
    this.mediaWrapper = this.querySelector('[data-configurator-media-wrapper]');
    this.mediaImageElements = this.mediaWrapper.querySelectorAll('[data-configurator-media-image]');

    this.mediaImageNames = [];
    this.mediaImageElements.forEach((mediaImageElement) => {
      this.mediaImageNames.push(
        this.extractKeywordFromAltText(mediaImageElement.dataset.configuratorMediaImage),
      );
    });

    this.printWrapper = this.querySelector('[data-configurator-print-wrapper]');
    this.printOptions = this.printWrapper?.querySelectorAll('[data-configurator-print-option]');
    this.printValueInput = this.printWrapper?.querySelector('[data-configurator-print-value]');
    this.printClublogoValueInput = this.printWrapper?.querySelector(
      '[data-configurator-print-clublogo-value]',
    );

    this.playerInputWrapper = this.querySelector('[data-configurator-player-input-wrapper]');
    this.playerInput = this.playerInputWrapper?.querySelector('[data-configurator-player-input]');
    this.playerInputButton = this.playerInputWrapper?.querySelector(
      '[data-configurator-player-input-button]',
    );
    this.playerInputSelect = this.playerInputWrapper?.querySelector(
      '[data-configurator-player-input-select]',
    );
    this.playerInputOptions = this.playerInputWrapper?.querySelectorAll(
      '[data-configurator-player-input-option]',
    );

    // Autocomplete inputs for flocking options
    this.autocompleteInputs = this.querySelectorAll('autocomplete-input');

    this.customInputWrapper = this.querySelector('[data-configurator-custom-input-wrapper]');
    this.customInputNumber = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-number]',
    );
    this.customInputName = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-name]',
    );
    this.customInputLimitCharcount = this.customInputWrapper?.querySelector(
      '[data-configurator-custom-input-limit-charcount]',
    );
    this.customInputBlacklistErrorMessage = this.customInputWrapper?.querySelector(
      '[data-configurator-blacklist-error-message]',
    );

    this.logosWrapper = this.querySelector('[data-configurator-logos-wrapper]');
    this.logosOptions = this.logosWrapper?.querySelectorAll('[data-configurator-logos-option]');
    this.logosValueInput = this.logosWrapper?.querySelector('[data-configurator-logos-value]');

    this.badgeInputCheckbox = this.querySelector('[data-configurator-option-input-badge]');

    this.additionalProductElements = this.querySelectorAll(
      '[data-configurator-additional-product]',
    );

    this.priceRegularElement = this.querySelector('[data-configurator-price-regular]');
    this.priceRegular = parseInt(this.priceRegularElement.dataset.configuratorPriceRegularValue);
    this.priceConfiguration = this.priceRegular;

    // Check if compare at price is available
    this.priceCompareElement = this.querySelector('[data-configurator-price-compare]');
    if (this.priceCompareElement) {
      this.priceCompare = parseInt(this.priceCompareElement.dataset.configuratorPriceCompareValue);
      this.priceConfigurationCompare = this.priceCompare;
    }

    // Check for prefill flocking URL parameters
    this.flockingUrlParameters = this.getFlockingUrlParameters();
    if (this.flockingUrlParameters.playerNumber) {
      this.handlePlayerFlockingPrefill(this.flockingUrlParameters.playerNumber);
    } else if (this.flockingUrlParameters.individualName) {
      this.handleCustomFlockingPrefill(this.flockingUrlParameters.individualName);
    }

    //  Bind events
    this.bindEvents();
  }

  getFlockingUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
      individualName: urlParams.get('individual_name'),
      playerNumber: urlParams.get('player_number'),
    };
  };

  /**
   * Programmatically prefills the custom flocking option with a given name
   * This method simulates user interaction by selecting the custom print option
   * and setting the name input field, triggering all necessary events and UI updates
   *
   * @param {string} name - The custom name to prefill (max 14 characters)
   */
  handleCustomFlockingPrefill = (name) => {
    // Find the custom print option radio button from available options
    const control = Array.from(this.printOptions).find((option) => option.value === 'custom');

    // Process and clean the input name, ensuring it's a valid string
    const nameProcessed = name && typeof name === 'string' ? name.trim() : '';

    // Validate name length (must be between 1-14 characters)
    const isValidName = nameProcessed.length > 0 && nameProcessed.length <= 14;

    // Return if there is no control, an invalid name or no input
    if (!control || !isValidName || !this.customInputName) {
      return;
    }

    // Check the custom print option radio button
    control.checked = true;

    // Trigger the print option selection logic (shows custom input wrapper, etc.)
    this.handlePrintOptionInput({ target: control });

    // Set the processed name in the input field
    this.customInputName.value = nameProcessed;

    // Trigger input event to properly handle the change (validation, character count, etc.)
    this.customInputName.dispatchEvent(new Event('input', { bubbles: true }));

    // Ensure preview updates immediately on the jersey
    this.togglePreviewElementClublogo(true); // Show club logo
    this.renderNamePreview(nameProcessed); // Display the name on jersey
  };

  /**
   * Programmatically prefills the player flocking option with a specific player
   * This method simulates user interaction by selecting the player print option
   * and choosing a player from the autocomplete options
   *
   * @param {string} number - The player number to select from available options
   */
  handlePlayerFlockingPrefill = (number) => {
    // Find the player print option radio button from available options
    const control = Array.from(this.printOptions).find((option) => option.value === 'player');

    // Check if the provided player number exists in the available player options
    const playerNumberExists = this.playerInput?.allOptions.some(
      (option) => option.number === number,
    );

    // Return if there is no control, no matching player number or no input
    if (!control || !playerNumberExists || !this.playerInput) {
      return;
    }

    // Check the player print option radio button
    control.checked = true;

    // Trigger the print option selection logic (shows player input wrapper, etc.)
    this.handlePrintOptionInput({ target: control });

    // Find the complete player data object using the number
    const player = this.playerInput.allOptions.find((option) => option.number === number);

    if (player) {
      const playerId = player.id;

      // Handle the player selection (updates preview, configuration values)
      this.handlePlayerFlockingChange(player, this.playerInput);

      // Recalculate the total price with the selected player's price
      this.calculateNewTotalPrice();

      // Update the autocomplete UI to show the selected player
      this.playerInput.setOption(playerId);
    }
  };

  /**
   * Extract keyword from alt text (e.g., "Jersey front view [front]" -> "front")
   * Falls back to original alt text if no brackets found
   * @param {String} altText
   * @return {String}
   */
  extractKeywordFromAltText = (altText) => {
    if (!altText) return '';

    // Look for text in brackets like [front], [back]
    const match = altText.match(/\[([^\]]+)\]/);
    return match ? match[1] : altText;
  };

  /**
   * Strip bracketed keywords from alt text for screen readers
   * @param {String} altText
   * @return {String}
   */
  stripKeywordFromAltText = (altText) => {
    if (!altText) return '';

    // Remove bracketed keywords and trim whitespace
    return altText.replace(/\s*\[[^\]]+\]\s*/g, '').trim();
  };

  /**
   * Bind the elements
   */
  bindEvents = () => {
    document.addEventListener('variant-change-complete', this.handleVariantChange.bind(this));

    // Handle printOption radio input change
    this.printOptions?.forEach((printOption) => {
      printOption.addEventListener('change', this.handlePrintOptionInput.bind(this));
    });

    // Handle logoOption checkbox input change
    this.logosOptions?.forEach((logosOption) => {
      logosOption.addEventListener('change', this.handleLogoOptionInput.bind(this));
    });

    this.autocompleteInputs?.forEach((autocompleteInput) => {
      autocompleteInput.addEventListener(
        'autocomplete:change',
        this.handleAutocompleteChange.bind(this),
      );
      autocompleteInput.addEventListener(
        'autocomplete:clear',
        this.handleAutocompleteClear.bind(this),
      );
    });

    this.customInputNumber?.addEventListener('input', this.handleCustomNumberInput.bind(this));
    this.customInputName?.addEventListener('input', this.handleCustomNameInput.bind(this));
    this.playerInputOptions?.forEach((playerInputOption) => {
      playerInputOption.addEventListener('click', (event) => {
        event.preventDefault();
        this.handlePlayerInput(playerInputOption);
      });
      // Add keyboard support for player options
      playerInputOption.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handlePlayerInput(playerInputOption);
        }
      });
    });
    this.badgeInputCheckbox?.addEventListener('change', this.handleBadgeInputChange.bind(this));
    this.productFormElement
      .querySelector('form')
      .addEventListener('submit', this.handleAddToCart.bind(this));
  };

  /**
   * Handle variant change
   */
  handleVariantChange = () => {
    this.priceRegularElement = this.querySelector('[data-configurator-price-regular]');
    this.priceCompareElement = this.querySelector('[data-configurator-price-compare]');

    this.calculateNewTotalPrice();
  };

  /**
   * Handle change event on the print options input
   */
  handlePrintOptionInput = (event) => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    // Hide all custom inputs by default
    this.playerInputWrapper.classList.add('hidden');
    this.customInputWrapper.classList.add('hidden');

    // Reset the preview
    this.resetPreview();

    // Guard: Don't show any inputs if print option value is none.
    if (event.target.value === 'none') {
      this.updateConfigurationValuePrint('', '');
      this.calculateNewTotalPrice();
      return;
    }

    // Actions based on input choice
    switch (event.target.value) {
      case 'player':
        // Show the player input element
        this.playerInputWrapper.classList.remove('hidden');

        // Set focus to the player selection dropdown button
        setTimeout(() => {
          if (this.playerInputButton) {
            this.playerInputButton.click();
          }

          // Focus the first player item
          const firstPlayerItem = this.playerInputSelect?.querySelector('a');
          if (firstPlayerItem) {
            firstPlayerItem.focus();
          }
        }, 100);

        // Check if player is filled in already and add this ID already OR if this is the first time, remove any value
        if (this.playerInput.dataset.configuratorPlayerInputId !== '') {
          this.togglePreviewElementClublogo(true);
          this.renderNumbersPreview(this.playerInput.dataset.configuratorPlayerInputNumber);
          this.renderNamePreview(this.playerInput.dataset.configuratorPlayerInputName);

          this.updateConfigurationValuePrint(
            this.playerInput.dataset.configuratorPlayerInputId,
            this.playerInput.dataset.configuratorPlayerInputPrice,
          );
        } else {
          this.updateConfigurationValuePrint('', '');
        }

        // Calculate the new total price
        this.calculateNewTotalPrice();

        break;
      case 'custom':
        // Show the custom input element
        this.customInputWrapper.classList.remove('hidden');

        // Set focus to the first custom input field (jersey number)
        setTimeout(() => {
          if (this.customInputNumber) {
            this.customInputNumber.focus();
          }
        }, 100);

        // Add custom print product variant ID to element
        this.updateConfigurationValuePrint(
          event.target.dataset.configuratorPrintOptionCustomId,
          parseInt(event.target.dataset.configuratorPrintOptionCustomPrice),
        );

        // Calculate the new total price
        this.calculateNewTotalPrice();

        break;
    }
  };

  /**
   * Handle change event on the logo options input
   * @param {Event} event
   */
  handleLogoOptionInput = (event) => {
    // Set variable to see if at least one checkbox is checked
    const checkedOne = Array.prototype.slice.call(this.logosOptions).some((x) => x.checked);

    // Guard: if no checkboxes are set, select the none option and return
    if (!checkedOne) {
      this.logosOptions[0].checked = true;
      this.hidePreviewLogos();
      this.updateConfigurationValueLogos('', '');
      this.calculateNewTotalPrice();
      return;
    }

    // Check: unselect the none option when at least one other checkbox is checked, also handle the previews
    if (event.target.value !== 'none') {
      this.logosOptions[0].checked = false;
      this.hidePreviewLogos();
      this.logosWrapper
        .querySelectorAll('[data-configurator-logos-option]:checked')
        .forEach((checkedLogosOption) => {
          this.showPreviewLogos(checkedLogosOption.value);
        });

      // Update the logos input value with the correct variant id(s)
      if (event.target.checked && !this.logosValueInput.value.includes(event.target.value)) {
        const newInputValue = `${this.logosValueInput.value},${event.target.value}`;
        const newInputPrice = `${this.logosValueInput.dataset.configuratorLogosPrice},${event.target.dataset.configuratorLogosOptionPrice}`;
        this.updateConfigurationValueLogos(newInputValue, newInputPrice);
      }
      if (
        event.target.checked === false &&
        this.logosValueInput.value.includes(event.target.value)
      ) {
        const logoIdSubstr = `,${event.target.value}`;
        const logoPriceSubstr = `,${event.target.dataset.configuratorLogosOptionPrice}`;
        this.updateConfigurationValueLogos(
          this.logosValueInput.value.replace(logoIdSubstr, ''),
          this.logosValueInput.dataset.configuratorLogosPrice.replace(logoPriceSubstr, ''),
        );
      }
    } else {
      this.logosOptions.forEach((logosOption) => {
        logosOption.checked = false;
      });
      this.logosOptions[0].checked = true;
      this.hidePreviewLogos();
      this.updateConfigurationValueLogos('', '');
    }

    this.calculateNewTotalPrice();
  };

  /**
   * Handle player input
   * @param {Object} target
   */
  handlePlayerInput = (target) => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    // Remove error class if applicable
    this.playerInputButton.classList.remove('border-danger');
    this.playerInputButton.classList.add('border-gray-3');

    // Render the previews
    this.togglePreviewElementClublogo(true);
    const isTextOnlyFlocking = target.dataset.configuratorPlayerInputOptionIsTextOnly == 'true';
    if (isTextOnlyFlocking) {
      this.renderNumbersPreview('');
      this.renderNamePreview('');
    } else {
      this.renderNumbersPreview(target.dataset.configuratorPlayerInputOptionNumber);
      this.renderNamePreview(target.dataset.configuratorPlayerInputOptionName);
    }

    // Update the custom inputs button value and close the dropdown
    this.playerInputButton.querySelector('.field__dropdown-label').innerHTML = target.innerHTML;
    this.playerInputSelect.classList.add('field__dropdown--hidden');

    // Update the player input id + data
    this.playerInput.dataset.configuratorPlayerInputId =
      target.dataset.configuratorPlayerInputOption;
    this.playerInput.dataset.configuratorPlayerInputPrice =
      target.dataset.configuratorPlayerInputOptionPrice;
    this.playerInput.dataset.configuratorPlayerInputNumber =
      target.dataset.configuratorPlayerInputOptionNumber;
    this.playerInput.dataset.configuratorPlayerInputName =
      target.dataset.configuratorPlayerInputOptionName;
    this.updateConfigurationValuePrint(
      target.dataset.configuratorPlayerInputOption,
      target.dataset.configuratorPlayerInputOptionPrice,
    );

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle custom number input
   */
  handleCustomNumberInput = () => {
    // Slide to the back fo the shirt
    this.slideToByAltName('back');

    // Remove any field errors if applicable
    this.customInputNumber.classList.remove('field__invalid');

    // Hide error message and enable add to cart button by default
    this.toggleBlacklistErrorMessage(false);
    this.variantRadiosElement?.toggleAddButton(false, '', false);

    // Guard: return if input is empty
    if (!this.customInputNumber.value.match(/^[0-9]+$/)) {
      this.customInputNumber.value = '';

      const allElements = this.getAllPreviewElements();
      allElements.numbers.forEach((numbersElement) => {
        if (numbersElement) {
          numbersElement.innerHTML = '';
        }
      });

      // Check: if custom input name is also empty, remove logo
      if (this.customInputName.value.length <= 0) {
        this.togglePreviewElementClublogo(false);
      }
      return;
    }

    // Guard: check if the value isn't on the blacklist
    if (this.options.blacklistNumbers.includes(this.customInputNumber.value)) {
      this.toggleBlacklistErrorMessage(true);
      this.variantRadiosElement?.toggleAddButton(true, '', false);
      return;
    }

    // Render the previews
    this.togglePreviewElementClublogo(true);
    this.renderNumbersPreview(this.customInputNumber.value);
  };

  /**
   * Handle custom name input
   */
  handleCustomNameInput = () => {
    // Slide to the back of the shirt
    this.slideToByAltName('back');

    // Remove any field errors if applicable
    this.customInputName.classList.remove('field__invalid');

    // Hide error message and enable add to cart button by default
    this.toggleBlacklistErrorMessage(false);
    this.variantRadiosElement?.toggleAddButton(false, '', false);

    // Check: if customInputName is empty, return
    if (this.customInputName.value.length <= 0) {
      const allElements = this.getAllPreviewElements();
      allElements.name.forEach((nameElement) => {
        if (nameElement) {
          nameElement.innerHTML = '';
        }
      });

      // Check: if number is also empty, remove logo
      if (this.customInputNumber.value.length <= 0) {
        this.togglePreviewElementClublogo(false);
      }

      // Update the custom input charcount
      this.updateCustomInputCharcount(0);
      return;
    }

    // Guard: check if the value isn't on the blacklist
    const customNameInputDowncase = this.customInputName.value.toLowerCase();
    if (this.options.blacklistNames.includes(customNameInputDowncase)) {
      this.toggleBlacklistErrorMessage(true);
      this.variantRadiosElement?.toggleAddButton(true, '', false);
      return;
    }

    // Render the previews
    this.togglePreviewElementClublogo(true);
    this.renderNamePreview(this.customInputName.value);

    // Update the custom input charcount
    this.updateCustomInputCharcount(this.customInputName.value.length);
  };

  /**
   * Update custom input limit character count
   * @param {Number} count
   */
  updateCustomInputCharcount = (count) => {
    this.customInputLimitCharcount.innerHTML = count;
  };

  /**
   * Handle autocomplete input selection for flocking options
   * @param {CustomEvent} event
   */
  handleAutocompleteChange = (event) => {
    const { option } = event.detail;
    const autocompleteElement = event.target;
    const flockingType = autocompleteElement.dataset.configuratorFlockingType;

    // Slide to the back of the shirt to show flocking
    this.slideToByAltName('back');

    // Handle different types of flocking
    switch (flockingType) {
      case 'district':
      case 'location':
      case 'player':
        this.handlePlayerFlockingChange(option, autocompleteElement);
        break;
      default:
        console.warn('Unknown flocking type:', flockingType);
    }

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle autocomplete input clearing for flocking options
   * @param {CustomEvent} event
   */
  handleAutocompleteClear = (event) => {
    const autocompleteElement = event.target;
    const flockingType = autocompleteElement.dataset.configuratorFlockingType;

    // Handle different types of flocking
    switch (flockingType) {
      case 'district':
      case 'location':
      case 'player':
        this.handlePlayerFlockingClear(autocompleteElement);
        break;
      default:
        console.warn('Unknown flocking type:', flockingType);
    }

    // Calculate the new total price
    this.calculateNewTotalPrice();
  };

  /**
   * Handle player flocking selection (alternative to dropdown)
   * @param {Object} option
   * @param {HTMLElement} autocompleteElement
   */
  handlePlayerFlockingChange = (option, autocompleteElement) => {
    // Render the previews similar to existing player input
    this.togglePreviewElementClublogo(true);

    const isTextOnlyFlocking = option.isTextOnly === true || option.isTextOnly === 'true';
    if (isTextOnlyFlocking) {
      this.renderNumbersPreview('');
      this.renderNamePreview('');
    } else {
      this.renderNumbersPreview(option.number || '');
      this.renderNamePreview(option.name);
    }

    // Update the configuration value for printing
    this.updateConfigurationValuePrint(option.id, option.price || 0);

    // Update the player input id + data
    this.playerInput.dataset.configuratorPlayerInputId = option.id;
    this.playerInput.dataset.configuratorPlayerInputPrice = option.price || 0;
    this.playerInput.dataset.configuratorPlayerInputNumber = option.number || '';
    this.playerInput.dataset.configuratorPlayerInputName = option.name || '';
  };

  /**
   * Handle player flocking clearing
   * @param {HTMLElement} autocompleteElement
   */
  handlePlayerFlockingClear = (autocompleteElement) => {
    // Clear the previews
    this.renderNumbersPreview('');
    this.renderNamePreview('');
    this.togglePreviewElementClublogo(false);

    // Clear the configuration value
    this.updateConfigurationValuePrint('', '');

    // Clear the player input data
    this.playerInput.dataset.configuratorPlayerInputId = '';
    this.playerInput.dataset.configuratorPlayerInputPrice = '';
    this.playerInput.dataset.configuratorPlayerInputNumber = '';
    this.playerInput.dataset.configuratorPlayerInputName = '';
  };

  /**
   * Navigate to image by alt name
   * @param {String} altName
   */
  slideToByAltName = (altName) => {
    const targetImage = this.mediaWrapper.querySelector(`[data-configurator-image="${altName}"]`);
    if (targetImage) {
      // If we're on mobile we need to slide the swiper to the correct image
      if (window.innerWidth < MOBILE_BREAKPOINT_WIDTH) {
        if (this.productMediaElement && this.productMediaElement.swiperGetActiveIndex) {
          const activeSlideIndex = this.productMediaElement.swiperGetActiveIndex();
          const targetIndex = this.mediaImageNames.indexOf(altName);
          if (activeSlideIndex !== targetIndex && targetIndex !== -1) {
            this.productMediaElement.swiperSlideTo(targetIndex);
          }
        }
      } else {
        // On desktop we simply scroll to it
        targetImage.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }
  };

  /**
   * Render numbers preview
   * @param {String} input
   */
  renderNumbersPreview = (input) => {
    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Update all numbers preview elements
    allElements.numbers.forEach((numbersElement) => {
      if (numbersElement) {
        numbersElement.innerHTML = '';

        for (let i = 0; i < input.length; i++) {
          // Create image element and set attributes
          const numberImageElement = document.createElement('img');
          numberImageElement.setAttribute('src', this.options.numberImageSources[input[i]]);
          numberImageElement.setAttribute('class', 'block max-h-[100%] px-2');

          numbersElement.appendChild(numberImageElement);
        }
      }
    });
  };

  /**
   * Render the preview
   * @param {String} input
   */
  renderNamePreview = (input) => {
    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Update all name preview elements
    allElements.name.forEach((nameElement) => {
      if (nameElement) {
        nameElement.innerHTML = input;
      }
    });
  };

  /**
   * Toggle the preview clublogo element
   * @param {Boolean} showBoolean
   */
  togglePreviewElementClublogo = (showBoolean) => {
    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Update all clublogo preview elements
    allElements.clublogo.forEach((clublogoElement) => {
      if (clublogoElement) {
        // Check if media image preview logo needs to be shown or hidden
        if (showBoolean) {
          clublogoElement.classList.remove('hidden');
          // Update the configuration value using the first element (they should all have the same data)
          if (clublogoElement === allElements.clublogo[0]) {
            this.updateConfigurationValueClublogoPrint(
              clublogoElement.dataset.configuratorMediaImagePreviewClublogoValue,
            );
          }
        } else {
          clublogoElement.classList.add('hidden');
          // Update the configuration value using the first element
          if (clublogoElement === allElements.clublogo[0]) {
            this.updateConfigurationValueClublogoPrint('');
          }
        }
      }
    });
  };

  /**
   * Show the chosen logos on the preview
   * @param {String} logoId
   */
  showPreviewLogos = (logoId) => {
    // Go to the front of the shirt
    this.slideToByAltName('front');

    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Update all logo preview elements
    allElements.logos.forEach((logoElement) => {
      if (logoElement && logoElement.dataset.configuratorLogoValue === logoId) {
        logoElement.classList.remove('hidden');
      }
    });
  };

  /**
   * Hide all logos on the preview
   */
  hidePreviewLogos = () => {
    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Hide all logo preview elements
    allElements.logos.forEach((logoElement) => {
      if (logoElement) {
        logoElement.classList.add('hidden');
      }
    });
  };

  /**
   * Toggle the members badge on the preview
   */
  handleBadgeInputChange = () => {
    // Go to the front of the shirt
    this.slideToByAltName('front');

    // Get all preview elements from both desktop and mobile layouts
    const allElements = this.getAllPreviewElements();

    // Update all badge preview elements
    allElements.badge.forEach((badgeElement) => {
      if (badgeElement) {
        // Show / Hide the members badge in the preview
        if (this.badgeInputCheckbox.checked) {
          badgeElement.classList.remove('hidden');
        } else {
          badgeElement.classList.add('hidden');
        }
      }
    });
  };

  /**
   * Update configuration value - Clublogo print
   * @param {String} configurationClublogoPrintValue
   */
  updateConfigurationValueClublogoPrint = (configurationClublogoPrintValue) => {
    this.printClublogoValueInput.value = configurationClublogoPrintValue;
  };

  /**
   * Update configuration value - Print
   * @param {String} configurationPrintValue
   * @param {Number} configurationPrintPrice
   */
  updateConfigurationValuePrint = (configurationPrintValue, configurationPrintPrice) => {
    this.printValueInput.value = configurationPrintValue;
    this.printValueInput.dataset.configuratorPrintPrice = configurationPrintPrice;
  };

  /**
   * Update configuration value - Logos
   * @param {String} configurationLogosValue
   */
  updateConfigurationValueLogos = (configurationLogosValue, configurationLogosPrice) => {
    this.logosValueInput.value = configurationLogosValue;
    this.logosValueInput.dataset.configuratorLogosPrice = configurationLogosPrice;
  };

  /**
   * Toggle the blacklist error message
   * @param {Boolean} showBoolean
   */
  toggleBlacklistErrorMessage = (showBoolean = false) => {
    showBoolean
      ? this.customInputBlacklistErrorMessage.classList.remove('hidden')
      : this.customInputBlacklistErrorMessage.classList.add('hidden');
  };

  /**
   * Reset preview
   */
  resetPreview = () => {
    // Remove the clublogo
    this.togglePreviewElementClublogo(false);

    // Clear the custom inputs
    this.customInputNumber.value = '';
    this.renderNumbersPreview('');
    this.customInputName.value = '';
    this.renderNamePreview('');
  };

  /**
   * Handle the add to cart call.
   */
  handleAddToCart = () => {
    // Guard: if player / custom print is filled in correctly if applicable
    if (!this.canSubmitProductForm()) {
      return;
    }

    const submitButton = this.productFormElement.querySelector('[type="submit"]');
    const mainProductId = this.productFormElement.querySelector('[name=id]').value;
    const mainProductCustomPriceElement = this.productFormElement.querySelector(
      '[data-configurator-custom-member-price]',
    );
    const uniqueTimestamp = Date.now();
    const uniqueTimestampString = uniqueTimestamp.toString();

    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('button--loading');

    const sectionsToFetch = [
      ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
      ...this.cartNotification.getSectionsToRender().map((section) => section.id),
    ];

    let customPriceProperty = {};
    if (mainProductCustomPriceElement) {
      customPriceProperty = {
        _member_custom_price: mainProductCustomPriceElement.value,
      };
    }

    const allItems = [
      {
        id: mainProductId,
        quantity: 1,
        properties: {
          _masterID: uniqueTimestampString,
          ...customPriceProperty,
        },
      },
    ];

    // Push configuration product items if applicable
    allItems.push(...this.getConfigurationProductFormItems(uniqueTimestampString));

    // Check if there are additional products that need to be added in the cart.
    if (this.additionalProductElements.length > 0) {
      this.additionalProductElements.forEach((additionalProductElement) => {
        if (additionalProductElement.checked) {
          // Add additional product to the array
          allItems.push({
            id: additionalProductElement.dataset.configuratorAdditionalProduct,
            quantity: 1,
            properties: {},
          });
        }
      });
    }

    // Check: Remove masterID property if we are only submitting the plain jersey
    // if (allItems.length === 1) {
    //   delete allItems[0].properties;
    // }

    // Assign the body for the call
    const body = JSON.stringify({
      items: allItems,
      sections: sectionsToFetch,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_url}/add.js`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status == 422) {
          // When quantity error
          this.cartNotification.setActiveElement(document.activeElement);
          this.cartNotification.renderQuantityError(
            this.productFormElement.querySelector('[name=inventory_quantity]').value,
          );
          return;
        }

        this.cartDrawer.renderContents(parsedState);
        setTimeout(() => {
          this.cartDrawer.open(submitButton);
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        submitButton.classList.remove('button--loading');
        submitButton.removeAttribute('disabled');
      });
  };

  /**
   * Helper function to check if the product form can be submitted or needs to show errors to certain input fields
   * @return {Boolean}
   */
  canSubmitProductForm = () => {
    const selectedPrintValue = this.printWrapper?.querySelector(
      '[data-configurator-print-option]:checked',
    ).value;

    if (selectedPrintValue === 'player' || selectedPrintValue === 'custom') {
      if (
        selectedPrintValue === 'player' &&
        this.playerInput.dataset.configuratorPlayerInputId === ''
      ) {
        this.playerInputButton.classList.remove('border-gray-3');
        this.playerInputButton.classList.add('border-danger');
        return false;
      }
      if (
        selectedPrintValue === 'custom' &&
        (this.customInputNumber.value === '' || this.customInputName.value === '')
      ) {
        if (this.customInputNumber.value === '') {
          this.customInputNumber.classList.add('field__invalid');
        }
        if (this.customInputName.value === '') {
          this.customInputName.classList.add('field__invalid');
        }
        return false;
      }
    }

    return true;
  };

  /**
   * Get all the additional configuration product items and return in array
   * @param {String} uniqueTimestampString
   * @return {Object}
   */
  getConfigurationProductFormItems = (uniqueTimestampString) => {
    const configurationItems = [];

    // Check if clublogo is filled in
    if (this.printClublogoValueInput && this.printClublogoValueInput.value !== '') {
      configurationItems.push({
        id: this.printClublogoValueInput?.value,
        quantity: 1,
        properties: {
          _linkedID: uniqueTimestampString,
        },
      });
    }

    // Check if print value is filled in
    if (this.printValueInput && this.printValueInput.value !== '') {
      const selectedPrintValue = this.printWrapper?.querySelector(
        '[data-configurator-print-option]:checked',
      ).value;

      let customInputProperties = {};
      if (selectedPrintValue === 'custom') {
        customInputProperties[
          '_print_value'
        ] = `custom ["${this.customInputNumber.value} ${this.customInputName.value}"]`;
        customInputProperties['_print_color'] = this.options.printColor;
      }

      configurationItems.push({
        id: this.printValueInput?.value,
        quantity: 1,
        properties: {
          _linkedID: uniqueTimestampString,
          ...customInputProperties,
        },
      });
    }

    // Check if one or more logo values are filled in
    if (this.logosValueInput && this.logosValueInput.value !== '') {
      let logosValue = this.logosValueInput?.value || '';

      if (logosValue[0] == ',') {
        logosValue = logosValue.substring(1);
      }

      const logosValueArr = logosValue.split(',');

      logosValueArr.forEach((logoValue) => {
        configurationItems.push({
          id: logoValue,
          quantity: 1,
          properties: {
            _linkedID: uniqueTimestampString,
          },
        });
      });
    }

    return configurationItems;
  };

  /**
   * Get the total price by current configuration
   */
  calculateNewTotalPrice = () => {
    // Reset the calculation price before calculating it
    this.priceConfiguration = this.priceRegular;
    this.priceConfigurationCompare = this.priceCompare || null;

    // Check if print value is filled in
    if (this.printValueInput && this.printValueInput.dataset.configuratorPrintPrice !== '') {
      this.priceConfiguration =
        this.priceConfiguration + parseInt(this.printValueInput?.dataset.configuratorPrintPrice);
      if (this.priceConfigurationCompare !== null) {
        this.priceConfigurationCompare =
          this.priceConfigurationCompare +
          parseInt(this.printValueInput?.dataset.configuratorPrintPrice);
      }
    }

    // Check if one or more logo values are filled in
    if (this.logosValueInput && this.logosValueInput.value !== '') {
      let logosPrice = this.logosValueInput?.dataset.configuratorLogosPrice || '';

      if (logosPrice[0] == ',') {
        logosPrice = logosPrice.substring(1);
      }

      const logosPriceArr = logosPrice.split(',');

      logosPriceArr.forEach((logoPrice) => {
        this.priceConfiguration = this.priceConfiguration + parseInt(logoPrice);
        if (this.priceConfigurationCompare !== null) {
          this.priceConfigurationCompare = this.priceConfigurationCompare + parseInt(logoPrice);
        }
      });
    }

    // Update the total price
    this.updateTotalPrice();
  };

  /**
   * Update the total price of the configurator product.
   */
  updateTotalPrice = () => {
    // Update the price in the correct format
    this.priceRegularElement.innerHTML = formatMoney(
      this.priceConfiguration,
      window.shop.moneyFormat,
    );

    // Update the compare at price if available
    if (this.priceCompareElement) {
      this.priceCompareElement.innerHTML = formatMoney(
        this.priceConfigurationCompare,
        window.shop.moneyFormat,
      );
    }
  };

  /**
   * Get all preview elements from both desktop and mobile layouts
   */
  getAllPreviewElements = () => {
    return {
      clublogo: this.mediaWrapper.querySelectorAll(
        '[data-configurator-media-image-preview-clublogo]',
      ),
      numbers: this.mediaWrapper.querySelectorAll(
        '[data-configurator-media-image-preview-numbers]',
      ),
      name: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-name]'),
      logos: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-logo]'),
      badge: this.mediaWrapper.querySelectorAll('[data-configurator-media-image-preview-badge]'),
    };
  };
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
